import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Monitor, AlertTriangle, Globe, Link2,
  Settings, Activity, Shield, Calendar, Key, CreditCard, Users, HelpCircle, Code2
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { plansAPI } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/monitors', icon: Monitor, label: 'Monitors' },
  { path: '/incidents', icon: AlertTriangle, label: 'Incidents' },
  { path: '/status-pages', icon: Globe, label: 'Status Pages' },
  { path: '/integrations', icon: Link2, label: 'Integrations' },
  { path: '/maintenance', icon: Calendar, label: 'Maintenance' },
  { path: '/plans', icon: CreditCard, label: 'Plans' },
  { path: '/settings', icon: Settings, label: 'Settings' },
  { path: '/api-docs', icon: Code2, label: 'API Reference' },
  { path: '/help', icon: HelpCircle, label: 'Help' },
];

export function Sidebar() {
  const location = useLocation();
  const { isAdmin } = useAuth();
  const { data: current } = useQuery({ queryKey: ['plan'], queryFn: plansAPI.current });
  const items = isAdmin
    ? [...navItems, { path: '/admin', icon: Users, label: 'Admin' }]
    : navItems;

  return (
    <motion.aside
      className="sidebar"
      initial={{ x: -260 }}
      animate={{ x: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        bottom: 0,
        width: 'var(--sidebar-width)',
        background: 'var(--color-bg-secondary)',
        borderRight: '1px solid var(--color-border)',
        padding: '0',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Logo */}
      <div style={{
        padding: 'var(--space-5) var(--space-5)',
        borderBottom: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
      }}>
        <div style={{
          width: 36,
          height: 36,
          borderRadius: 'var(--radius-md)',
          background: 'linear-gradient(135deg, #10B981, #059669)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Activity size={20} color="white" />
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 'var(--font-size-lg)', letterSpacing: '-0.025em' }}>
            Uptimer
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
            Monitoring
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, padding: 'var(--space-4) var(--space-3)', overflowY: 'auto' }}>
        {items.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius-md)',
                color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                background: isActive ? 'var(--color-accent-bg)' : 'transparent',
                textDecoration: 'none',
                fontSize: 'var(--font-size-sm)',
                fontWeight: isActive ? 600 : 400,
                marginBottom: 'var(--space-1)',
                transition: 'all var(--transition-fast)',
                position: 'relative',
              }}
            >
              {isActive && (
                <motion.div
                  layoutId="sidebar-indicator"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 3,
                    height: 20,
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-accent)',
                  }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
              <item.icon size={18} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{
        padding: 'var(--space-4) var(--space-5)',
        borderTop: '1px solid var(--color-border)',
        fontSize: 'var(--font-size-xs)',
        color: 'var(--color-text-muted)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <Shield size={14} />
          <span>{current ? `${current.plan.name} plan` : 'Loading plan…'}</span>
        </div>
      </div>
    </motion.aside>
  );
}
